"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={true}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-accent/50 data-[state=open]:bg-accent/50 text-foreground font-default">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-card border border-border">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none font-default text-foreground">
                              {child.label}
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-accent/50 text-foreground font-default")}>
                <Link to={item.href || "#"}>{item.label}</Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col p-4 space-y-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col space-y-2">
          <Link to={item.href || "#"} className="text-lg font-medium font-default py-2 border-b border-border/50 text-foreground" onClick={onItemClick}>
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="flex flex-col pl-4 space-y-2 mt-2">
              {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-base text-muted-foreground hover:text-primary py-1 font-default" onClick={onItemClick}>
                  {child.label}
                </Link>)}
            </div>}
        </div>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="259734" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 bg-background/95 backdrop-blur-sm text-foreground border-b border-primary/10", isScrolled && "bg-background/80 backdrop-blur-md shadow-lg border-primary/20")}>
      <div className="container mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-6 md:gap-8 lg:gap-10">
        
        <div className="flex items-center shrink-0 py-2 px-8 md:px-12 lg:px-14 brightness-[1.6] drop-shadow-[0_0_12px_rgba(218,180,105,0.6)] drop-shadow-[0_0_20px_rgba(218,180,105,0.3)] blur-none transition-transform duration-300 hover:scale-110">
          <Link to="/" className="flex items-center gap-2">
            <WvcLogo className="h-12 md:h-14 lg:h-16 w-auto flex-shrink-0" />
          </Link>
        </div>

        
        <div className="hidden md:flex flex-1 items-center justify-center px-4 md:px-6">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        
        <div className="hidden md:flex items-center gap-4 lg:gap-6 shrink-0">
          <Button variant="outline" className="h-9 font-default font-semibold border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-md shadow-sm" asChild>
            <Link to="/RequestAQuotePage">Let’s Showcase Your Course</Link>
          </Button>
        </div>

        
        <div className="flex md:hidden items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" className="text-foreground hover:bg-accent/50">
            {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>
      </div>

      
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-background/95 backdrop-blur-md border-b border-primary/20 shadow-2xl">
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-4 border-t border-border/50">
            <Button variant="default" className="w-full font-default font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" asChild>
              <Link to="/RequestAQuotePage">Let’s Showcase Your Course</Link>
            </Button>
          </div>
        </div>}
    </section>;
}